import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import { auth } from '../config/firebase';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';
const CAROUSEL_API_BASE_URL = process.env.REACT_APP_CAROUSEL_API_BASE_URL || 'https://carousel.dev.carouselshopai.com';

interface InviteCode {
  id: string;
  code: string;
  status: string;
  redeemedBy?: string;
  redeemedAt?: string;
  expiresAt?: string;
  createdBy: string;
  createdAt: string;
  notes?: string;
}

const STATUS_BADGE_STYLES: Record<string, React.CSSProperties> = {
  AVAILABLE: { backgroundColor: 'var(--success-bg)', color: 'var(--success-dark)' },
  REDEEMED: { backgroundColor: 'var(--bg-accent)', color: 'var(--primary-light)' },
  EXPIRED: { backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' },
  REVOKED: { backgroundColor: 'var(--error-bg)', color: 'var(--error-text)' },
};

const PlatformAdminTab: React.FC = () => {
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [inviteCodesLoading, setInviteCodesLoading] = useState(false);
  const [inviteCodeError, setInviteCodeError] = useState('');
  const [inviteCodeStatusFilter, setInviteCodeStatusFilter] = useState('');
  const [generateCount, setGenerateCount] = useState(5);
  const [generateExpiresInDays, setGenerateExpiresInDays] = useState(90);
  const [generateNotes, setGenerateNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  // Sub-tab state
  const [adminSubTab, setAdminSubTab] = useState<'invite-codes' | 'category-index'>('invite-codes');

  // Category Index state
  const [categoryShopId, setCategoryShopId] = useState('');
  const [categoryStatus, setCategoryStatus] = useState<any>(null);
  const [categoryStatusLoading, setCategoryStatusLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [rebuildResult, setRebuildResult] = useState<any>(null);
  const [rebuilding, setRebuilding] = useState(false);

  useEffect(() => {
    loadInviteCodes();
  }, []);

  const getAuthToken = async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  };

  const loadInviteCodes = async (statusFilter?: string) => {
    try {
      setInviteCodesLoading(true);
      setInviteCodeError('');

      const token = await getAuthToken();
      const queryParam = statusFilter ? `?status=${statusFilter}` : '';
      const response = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/admin/invite-codes${queryParam}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const codes: InviteCode[] = await response.json();
      setInviteCodes(codes);
    } catch (err: any) {
      console.error('Failed to load invite codes:', err);
      setInviteCodeError('Failed to load invite codes.');
    } finally {
      setInviteCodesLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    try {
      setGenerating(true);
      setInviteCodeError('');

      const token = await getAuthToken();
      const response = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/admin/invite-codes/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            count: generateCount,
            expiresInDays: generateExpiresInDays,
            notes: generateNotes || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      setGenerateNotes('');
      // Refresh the list
      await loadInviteCodes(inviteCodeStatusFilter || undefined);
    } catch (err: any) {
      console.error('Failed to generate invite codes:', err);
      setInviteCodeError('Failed to generate invite codes.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeCode = async (id: string) => {
    try {
      setInviteCodeError('');
      const token = await getAuthToken();
      const response = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/admin/invite-codes/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Refresh the list
      await loadInviteCodes(inviteCodeStatusFilter || undefined);
    } catch (err: any) {
      console.error('Failed to revoke invite code:', err);
      setInviteCodeError('Failed to revoke invite code.');
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setInviteCodeStatusFilter(status);
    loadInviteCodes(status || undefined);
  };

  // Category Index handlers
  const handleCheckSync = async () => {
    if (!categoryShopId.trim()) return;
    setCategoryStatusLoading(true);
    setCategoryError('');
    setCategoryStatus(null);
    setRebuildResult(null);

    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${CAROUSEL_API_BASE_URL}/api/v1/admin/shop-categories-status/${encodeURIComponent(categoryShopId.trim())}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }

      const data = await response.json();
      setCategoryStatus(data);
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to check sync status');
    } finally {
      setCategoryStatusLoading(false);
    }
  };

  const handleRebuild = async () => {
    if (!categoryShopId.trim()) return;
    setRebuilding(true);
    setCategoryError('');
    setRebuildResult(null);

    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${CAROUSEL_API_BASE_URL}/api/v1/admin/rebuild-shop-categories/${encodeURIComponent(categoryShopId.trim())}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }

      const data = await response.json();
      setRebuildResult(data);
      setCategoryStatus(null);
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to rebuild category index');
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div className="platform-admin-tab" style={{ fontSize: '14px' }}>
      {/* Header */}
      <div className="admin-header card mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="heading-secondary mb-2" style={{ fontSize: '16px' }}>Platform Administration</h3>
          </div>
          <div className="admin-badge">
            <span className="badge" style={{
              backgroundColor: 'var(--delft-light)',
              color: 'var(--text-primary)'
            }}>
              🛡️ Platform Admin
            </span>
          </div>
        </div>
        {/* Sub-tabs */}
        <div className="flex gap-1" style={{ marginTop: '12px' }}>
          <button
            onClick={() => setAdminSubTab('invite-codes')}
            className={`tab-button ${adminSubTab === 'invite-codes' ? 'active' : ''}`}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Invite Codes
          </button>
          <button
            onClick={() => setAdminSubTab('category-index')}
            className={`tab-button ${adminSubTab === 'category-index' ? 'active' : ''}`}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Category Indexes
          </button>
        </div>
      </div>

      {adminSubTab === 'invite-codes' && (
      <div className="admin-invite-codes">
          {/* Generate Codes Form */}
          <div className="card mb-6">
            <h4 className="heading-secondary mb-4">Generate Invite Codes</h4>
            <div className="flex gap-4 items-end" style={{ flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 auto' }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: '4px' }}>Count</label>
                <input
                  type="number"
                  className="form-input"
                  value={generateCount}
                  onChange={(e) => setGenerateCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  min={1}
                  max={100}
                  style={{ width: '80px', padding: '6px 10px', fontSize: '13px' }}
                />
              </div>
              <div style={{ flex: '0 0 auto' }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: '4px' }}>Expiry (days)</label>
                <input
                  type="number"
                  className="form-input"
                  value={generateExpiresInDays}
                  onChange={(e) => setGenerateExpiresInDays(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  style={{ width: '100px', padding: '6px 10px', fontSize: '13px' }}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: '4px' }}>Notes (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={generateNotes}
                  onChange={(e) => setGenerateNotes(e.target.value)}
                  placeholder="e.g., Beta testers batch 1"
                  style={{ width: '100%', padding: '6px 10px', fontSize: '13px' }}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleGenerateCodes}
                disabled={generating}
                style={{ whiteSpace: 'nowrap', padding: '8px 16px', fontSize: '13px', alignSelf: 'flex-end' }}
              >
                {generating ? 'Generating...' : `Generate ${generateCount} Code${generateCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {inviteCodeError && (
            <div className="error-banner card mb-6">
              <div className="text-error">{inviteCodeError}</div>
            </div>
          )}

          {/* Status Filter */}
          <div className="card mb-6">
            <div className="flex items-center justify-between">
              <h4 className="heading-secondary">Invite Codes</h4>
              <div className="flex gap-1">
                {[
                  { value: '', label: 'All' },
                  { value: 'AVAILABLE', label: 'Available' },
                  { value: 'REDEEMED', label: 'Redeemed' },
                  { value: 'EXPIRED', label: 'Expired' },
                  { value: 'REVOKED', label: 'Revoked' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => handleStatusFilterChange(filter.value)}
                    className={`tab-button ${inviteCodeStatusFilter === filter.value ? 'active' : ''}`}
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Codes List */}
          {inviteCodesLoading ? (
            <div className="card text-center py-12">
              <div className="spinner-sm" style={{ margin: '0 auto 12px' }}></div>
              <span className="text-muted">Loading invite codes...</span>
            </div>
          ) : inviteCodes.length === 0 ? (
            <div className="card text-center py-12">
              <div className="text-6xl mb-4">🎟️</div>
              <h3 className="heading-secondary text-xl mb-2">No invite codes found</h3>
              <p className="text-muted">
                {inviteCodeStatusFilter
                  ? `No codes with status "${inviteCodeStatusFilter}"`
                  : 'Generate your first batch of invite codes above'}
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {inviteCodes.map((code) => (
                <div key={code.id} className="card">
                  <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: '12px' }}>
                    <div className="flex items-center gap-4" style={{ flex: '1 1 auto', minWidth: 0 }}>
                      <code style={{
                        fontFamily: 'monospace',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--bg-card)',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                      }}>
                        {code.code}
                      </code>
                      <span
                        className="badge"
                        style={{
                          ...STATUS_BADGE_STYLES[code.status] || {},
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {code.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted" style={{ flex: '1 1 auto', minWidth: 0, flexWrap: 'wrap', gap: '8px' }}>
                      <span>Created {formatDate(code.createdAt)}</span>
                      {code.expiresAt && (
                        <span>Expires {formatDate(code.expiresAt)}</span>
                      )}
                      {code.redeemedBy && (
                        <span>Redeemed by {code.redeemedBy}</span>
                      )}
                      {code.notes && (
                        <span style={{ fontStyle: 'italic' }}>{code.notes}</span>
                      )}
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      {code.status === 'AVAILABLE' && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleRevokeCode(code.id)}
                          style={{ color: 'var(--error-text)', borderColor: 'var(--error-border)' }}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      )}

      {adminSubTab === 'category-index' && (
      <div className="admin-category-index">
        <div className="card mb-6">
          <h4 className="heading-secondary mb-4">Shop Category Index</h4>
          <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '12px' }}>
            Check if a shop's Redis category index is in sync with BigQuery slides. Data may be up to 15 minutes behind Firestore.
          </p>
          <div className="flex gap-4 items-end" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 250px' }}>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: '4px' }}>Shop ID</label>
              <input
                type="text"
                className="form-input"
                value={categoryShopId}
                onChange={(e) => setCategoryShopId(e.target.value)}
                placeholder="Enter shop ID"
                style={{ width: '100%', padding: '6px 10px', fontSize: '13px' }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={handleCheckSync}
              disabled={!categoryShopId.trim() || categoryStatusLoading}
              style={{ whiteSpace: 'nowrap', padding: '8px 16px', fontSize: '13px', alignSelf: 'flex-end' }}
            >
              {categoryStatusLoading ? 'Checking...' : 'Check Sync'}
            </button>
            <button
              className="btn"
              onClick={handleRebuild}
              disabled={!categoryShopId.trim() || rebuilding}
              style={{
                whiteSpace: 'nowrap',
                padding: '8px 16px',
                fontSize: '13px',
                alignSelf: 'flex-end',
                backgroundColor: 'var(--warning-bg)',
                color: 'var(--warning-text)',
                border: '1px solid var(--warning)',
              }}
            >
              {rebuilding ? 'Rebuilding...' : 'Rebuild'}
            </button>
          </div>
        </div>

        {categoryError && (
          <div className="error-banner card mb-6">
            <div className="text-error">{categoryError}</div>
          </div>
        )}

        {categoryStatus && (
          <div className="card mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h4 className="heading-secondary">Sync Status</h4>
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: categoryStatus.inSync ? 'var(--success-bg)' : 'var(--error-bg)',
                color: categoryStatus.inSync ? 'var(--success-dark)' : 'var(--error-text)',
              }}>
                {categoryStatus.inSync ? 'In Sync' : 'Out of Sync'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Categories in BigQuery</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{categoryStatus.categoriesInBigQuery}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Categories in Redis</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{categoryStatus.categoriesInRedis}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Missing from Redis</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: categoryStatus.missingFromRedis > 0 ? 'var(--error-text)' : 'inherit' }}>
                  {categoryStatus.missingFromRedis}
                </div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Orphans in Redis</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: categoryStatus.orphansInRedis > 0 ? 'var(--warning-text)' : 'inherit' }}>
                  {categoryStatus.orphansInRedis}
                </div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Slides in Shop</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{categoryStatus.slidesInShop}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Redis Available</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{categoryStatus.redisAvailable ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
        )}

        {rebuildResult && (
          <div className="card mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h4 className="heading-secondary">Rebuild Result</h4>
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: 'var(--success-bg)',
                color: 'var(--success-dark)',
              }}>
                {rebuildResult.status}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>BigQuery Source</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{rebuildResult.categoriesInBigQuerySource}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Redis Before</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{rebuildResult.categoriesInRedisBefore}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Redis After</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{rebuildResult.categoriesInRedisAfter}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Added</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: rebuildResult.added > 0 ? 'var(--success-dark)' : 'inherit' }}>
                  +{rebuildResult.added}
                </div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Removed</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: rebuildResult.removed > 0 ? 'var(--error-text)' : 'inherit' }}>
                  -{rebuildResult.removed}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PlatformAdminTab;
